/**
 * LineHeightExtension — Zeilenabstand pro Paragraph als TipTap-Extension.
 *
 * Phase 8 Iter 3 (30.05.): Zeilenabstand-Presets fuer Signaturen.
 * Iter 5 (30.05.): 3 → 5 Presets (sehr eng / eng / normal / weit / sehr weit).
 * Iter 6 (30.05.): "kompakt" als 6. Preset — line-height:1 PLUS margin:0,
 *                  damit Multi-Zeilen-Signaturen direkt untereinander sitzen
 *                  ohne 16px Paragraph-Lueck dazwischen.
 *
 * Mail-Compat: Inline-`style="line-height:X[;margin:0]"` wird von allen
 * Mail-Clients (Gmail/Outlook/AppleMail) zuverlaessig respektiert.
 */
import { Extension } from '@tiptap/core';

// Spezial-Marker fuer "kompakt"-Modus — wird in renderHTML als
// line-height:1 + margin:0 expandiert, in parseHTML zurueckgeparsed.
export const TIGHT_VALUE = 'tight';

export const LINE_HEIGHT_PRESETS = [
  { label: 'kompakt', value: TIGHT_VALUE }, // line-height:1 + margin:0 (Zeilen direkt untereinander)
  { label: 'sehr eng', value: '1.0' },
  { label: 'eng', value: '1.2' },
  { label: 'normal', value: '' }, // leer = kein Inline-Style → EMAIL_CSS-Default (1.6)
  { label: 'weit', value: '1.9' },
  { label: 'sehr weit', value: '2.2' },
] as const;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    lineHeight: {
      /** Setzt line-height auf alle gewaehlten Bloecke. '' = unset. */
      setLineHeight: (value: string) => ReturnType;
      unsetLineHeight: () => ReturnType;
    };
  }
}

export const LineHeightExtension = Extension.create({
  name: 'lineHeight',

  addOptions() {
    return {
      types: ['paragraph', 'heading', 'listItem'] as string[],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (el: HTMLElement) => {
              const lh = (el.style?.lineHeight ?? '').trim();
              const margin = (el.style?.margin ?? '').trim();
              // kompakt-Erkennung: line-height:1 + margin:0
              if (
                (lh === '1' || lh === '1.0')
                && (margin === '0' || margin === '0px' || margin === '0 0 0 0')
              ) {
                return TIGHT_VALUE;
              }
              return lh || null;
            },
            renderHTML: (attrs: { lineHeight?: string | null }) => {
              if (!attrs.lineHeight) return {};
              if (attrs.lineHeight === TIGHT_VALUE) {
                // Zeilen direkt untereinander: line-height + Paragraph-Margin auf 0
                return { style: 'line-height: 1; margin: 0' };
              }
              return { style: `line-height: ${attrs.lineHeight}` };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (value: string) =>
        ({ commands }) => {
          return this.options.types.every((type: string) =>
            commands.updateAttributes(type, { lineHeight: value || null }),
          );
        },
      unsetLineHeight:
        () =>
        ({ commands }) => {
          return this.options.types.every((type: string) =>
            commands.resetAttributes(type, 'lineHeight'),
          );
        },
    };
  },
});
